import { z } from 'zod';
import type { Membership, Organization } from '../types/state';

export const businessInfoIndexSchema = z.object({
  declaredPepInBeneficiaries: z.boolean({ message: 'This field is required' }),
});

export type BusinessInfoIndexSchema = z.infer<typeof businessInfoIndexSchema>;

export const businessInfoFinancesSchema = z.object({
  initialOrganizationType: z.string().nonempty(),
  annualTurnover: z.string().nonempty(),
  yearlyTransactionVolume: z.string().nonempty(),
  maxSinglePaymentAmount: z.number().positive(),
});

export type BusinessInfoFinancesSchema = z.infer<typeof businessInfoFinancesSchema>;

export const usageSchema = z.object({
  natureOfOperations: z.array(z.string()).min(1, 'This field is required'),
});

export type UsageSchema = z.infer<typeof usageSchema>;

export function requiresLegalForm(organization: Organization): boolean {
  return organization.unreferenced === true;
}

const legalFormSchema = z.object({
  legalFormCreditsafeCode: z.string().nonempty(),
  legalRegistrationDate: z.string(),
});

export function hasLegalForm(organization: Organization): boolean {
  const isInfoValid = legalFormSchema.safeParse({
    legalFormCreditsafeCode: organization.legalFormCreditsafeCode,
    legalRegistrationDate: organization.legalRegistrationDate,
  }).success;

  // Document validation included but not enforced yet (as per Q4B)
  // const hasDocument = Boolean(organization.hasKbisDocument);

  return isInfoValid;
}

export function requiresUBOFlow(organization: Organization): boolean {
  if (organization.isPassiveNonFinancialEntity === true) {
    return true;
  }

  if (organization.declaredPepInBeneficiaries === true) {
    return true;
  }

  return false;
}

export function hasProvidedUBOs(memberships: Membership[]): boolean {
  const shareholders = memberships.filter(m => m.shareholder === true);

  if (shareholders.length === 0) {
    return false;
  }

  return shareholders.every(m => m.uboProviderStatus === 'confirmed');
}

export function hasUsage(organization: Organization): boolean {
  if (!organization.natureOfOperations) return false;

  if (Array.isArray(organization.natureOfOperations)) {
    return organization.natureOfOperations.length > 0;
  }

  return organization.natureOfOperations.trim().length > 0;
}
